import React, { useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import type { LatLngExpression, LeafletMouseEvent } from 'leaflet';
import L from 'leaflet';
import * as Icons from '@ant-design/icons';
import { renderToStaticMarkup } from 'react-dom/server.browser';
import { geoDebugLog, isGeoDebugEnabled } from '../../utils/geoDebug';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { parseFiniteNumber } from './geoMapUtils';

const markerDoubleClickState: {
  locationId: string | null;
  timerId: number | null;
  pendingSingleClickTimerId: number | null;
  suppressClickUntilMs: number;
} = {
  locationId: null,
  timerId: null,
  pendingSingleClickTimerId: null,
  suppressClickUntilMs: 0,
};

const GEO_DEBUG_PREFIX = '[GeoLocationMapView]';

type AntIconComponent = React.ComponentType<React.ComponentProps<typeof Icons.EnvironmentOutlined>>;

const clearMarkerDoubleClickState = () => {
  if (markerDoubleClickState.timerId !== null) {
    window.clearTimeout(markerDoubleClickState.timerId);
  }
  if (markerDoubleClickState.pendingSingleClickTimerId !== null) {
    window.clearTimeout(markerDoubleClickState.pendingSingleClickTimerId);
  }
  markerDoubleClickState.locationId = null;
  markerDoubleClickState.timerId = null;
  markerDoubleClickState.pendingSingleClickTimerId = null;
};

type GeoLocation = {
  id: string;
  name: string;
  latitude?: string | number | null;
  longitude?: string | number | null;
  description?: string | null;
  categoryIcon?: string | null;
  categoryColor?: string | null;
  isVirtualCoordinates?: boolean;
};

type GeoLocationMapViewProps = {
  locations: GeoLocation[];
  selectedLocationId?: string | null;
  onLocationClick?: (location: GeoLocation) => void;
  onLocationDoubleClick?: (location: GeoLocation) => void;
  canEdit?: boolean;
  onLocationMove?: (location: GeoLocation, lat: number, lon: number) => void;
  onMapBackgroundClick?: (lat: number, lon: number) => void;
  onMapClick?: (lat: number, lon: number) => void;
  height?: number | string;
  fallbackCenter?: { lat: number; lon: number } | null;
};

delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const getIconComponent = (iconName?: string | null) => {
  if (!iconName || !(iconName in Icons)) {
    return null;
  }
  const iconMap = Icons as unknown as Record<string, AntIconComponent>;
  const IconComponent = iconMap[iconName];
  return IconComponent ?? null;
};

const getDevicePixelRatioBucket = (): '1x' | '2x' => {
  if (typeof window === 'undefined') return '1x';
  return window.devicePixelRatio > 1 ? '2x' : '1x';
};

const TOKYO_STATION_FALLBACK_CENTER: LatLngExpression = [35.681236, 139.767125];

const RecenterOnDataChange: React.FC<{
  center: LatLngExpression;
  fallbackCenter?: { lat: number; lon: number } | null;
  selectedLocationId?: string | null;
  selectedLatLon?: { lat: number; lon: number } | null;
  markerBounds?: L.LatLngBounds | null;
  markerCount: number;
}> = ({ center, fallbackCenter, selectedLocationId, selectedLatLon, markerBounds, markerCount }) => {
  const map = useMap();
  const prevMarkerCountRef = useRef<number>(0);
  const prevCenterRef = useRef<LatLngExpression | null>(null);
  const prevBoundsKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const prevMarkerCount = prevMarkerCountRef.current;
    const prevCenter = prevCenterRef.current;

    const current = map.getCenter();
    const isAtTokyoFallback =
      Math.abs(current.lat - (TOKYO_STATION_FALLBACK_CENTER[0] as number)) < 0.000_5
      && Math.abs(current.lng - (TOKYO_STATION_FALLBACK_CENTER[1] as number)) < 0.000_5;

    const isCenterChanged = Boolean(prevCenter) && JSON.stringify(prevCenter) !== JSON.stringify(center);

    const boundsKey = markerBounds
      ? `${markerBounds.getSouthWest().lat.toFixed(6)},${markerBounds.getSouthWest().lng.toFixed(6)}|${markerBounds.getNorthEast().lat.toFixed(6)},${markerBounds.getNorthEast().lng.toFixed(6)}`
      : null;
    const boundsChanged = Boolean(boundsKey) && boundsKey !== prevBoundsKeyRef.current;

    prevMarkerCountRef.current = markerCount;
    prevCenterRef.current = center;
    prevBoundsKeyRef.current = boundsKey;

    // Selected marker (explicit intent) always wins.
    if (selectedLatLon) {
      geoDebugLog(GEO_DEBUG_PREFIX, 'recenter map (selected)', {
        markerCount,
        selectedLocationId,
        selectedLatLon,
        currentCenter: { lat: current.lat, lon: current.lng },
      });
      map.whenReady(() => {
        map.invalidateSize();
        map.setView([selectedLatLon.lat, selectedLatLon.lon], Math.max(map.getZoom(), 16), { animate: false });
      });
      return;
    }

    // When markers appear or their bounds change, fit to bounds (first-run + data refresh).
    if (markerBounds && markerCount > 0 && (prevMarkerCount === 0 || boundsChanged || isAtTokyoFallback)) {
      geoDebugLog(GEO_DEBUG_PREFIX, 'recenter map (fitBounds)', {
        markerCount,
        boundsKey,
        prevMarkerCount,
        boundsChanged,
        isAtTokyoFallback,
        currentCenter: { lat: current.lat, lon: current.lng },
      });
      map.whenReady(() => {
        map.invalidateSize();
        map.fitBounds(markerBounds, { padding: [40, 40], maxZoom: 18, animate: false });
      });
      return;
    }

    // If there are no markers, but we have a fallback center and we're still at Tokyo, move to fallback.
    if (markerCount === 0 && fallbackCenter && isAtTokyoFallback) {
      geoDebugLog(GEO_DEBUG_PREFIX, 'recenter map (fallback)', {
        markerCount,
        fallbackCenter,
        isCenterChanged,
        currentCenter: { lat: current.lat, lon: current.lng },
      });
      map.whenReady(() => {
        map.invalidateSize();
        map.setView(center, map.getZoom(), { animate: false });
      });
      return;
    }

    // If data says center changed but the map is still at Tokyo, move.
    if (isCenterChanged && isAtTokyoFallback) {
      geoDebugLog(GEO_DEBUG_PREFIX, 'recenter map (center changed)', {
        markerCount,
        center,
        currentCenter: { lat: current.lat, lon: current.lng },
      });
      map.whenReady(() => {
        map.invalidateSize();
        map.setView(center, map.getZoom(), { animate: false });
      });
    }

  }, [center, fallbackCenter, map, markerBounds, markerCount, selectedLatLon, selectedLocationId]);

  return null;
};

const InvalidateSizeOnContainerResize: React.FC = () => {
  const map = useMap();
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    const container = map.getContainer();
    if (!container) return;
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = window.requestAnimationFrame(() => {
        map.invalidateSize({ pan: false });
      });
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [map]);

  return null;
};

const StopLeafletAnimationsOnUnmount: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    return () => {
      map.stop();
    };
  }, [map]);

  return null;
};

export const GeoLocationMapView: React.FC<GeoLocationMapViewProps> = ({
  locations,
  selectedLocationId,
  onLocationClick,
  onLocationDoubleClick,
  canEdit = false,
  onLocationMove,
  onMapBackgroundClick,
  onMapClick,
  height = 560,
  fallbackCenter = null,
}) => {
  useEffect(() => {
    return () => {
      clearMarkerDoubleClickState();
    };
  }, []);

  const markerLocations = useMemo(() => {
    const mapped = locations.map((location) => {
      const lat = parseFiniteNumber(location.latitude);
      const lon = parseFiniteNumber(location.longitude);
      if (lat === null || lon === null) {
        return null;
      }
      return { location, lat, lon };
    });

    const filtered = mapped.filter((value): value is { location: GeoLocation; lat: number; lon: number } => value !== null);
    if (isGeoDebugEnabled()) {
      geoDebugLog(GEO_DEBUG_PREFIX, 'markerLocations computed', {
        locationsCount: locations.length,
        markerCount: filtered.length,
        sampleRaw: locations.slice(0, 3).map((l) => ({
          id: l.id,
          latitude: l.latitude,
          longitude: l.longitude,
          latType: typeof l.latitude,
          lonType: typeof l.longitude,
          parsedLat: parseFiniteNumber(l.latitude),
          parsedLon: parseFiniteNumber(l.longitude),
        })),
      });
    }
    return filtered;
  }, [locations]);

  const center: LatLngExpression = useMemo(() => {
    const selected = markerLocations.find((m) => m.location.id === selectedLocationId);
    if (selected) {
      return [selected.lat, selected.lon];
    }
    if (markerLocations.length > 0) {
      return [markerLocations[0].lat, markerLocations[0].lon];
    }
    if (fallbackCenter) {
      return [fallbackCenter.lat, fallbackCenter.lon];
    }
    return TOKYO_STATION_FALLBACK_CENTER;
  }, [fallbackCenter, markerLocations, selectedLocationId]);

  const selectedLatLon = useMemo(() => {
    const selected = markerLocations.find((m) => m.location.id === selectedLocationId);
    if (!selected) return null;
    return { lat: selected.lat, lon: selected.lon };
  }, [markerLocations, selectedLocationId]);

  const markerBounds = useMemo(() => {
    if (markerLocations.length === 0) return null;
    const points = markerLocations.map((m) => [m.lat, m.lon] as [number, number]);
    return L.latLngBounds(points);
  }, [markerLocations]);

  const markerIconByKey = useMemo(() => new Map<string, L.DivIcon>(), []);
  const devicePixelRatioBucket = useMemo(() => getDevicePixelRatioBucket(), []);
  const doubleClickWindowMs = 2000;
  const singleClickDelayMs = 220;
  const doubleClickStateRef = useRef(markerDoubleClickState);

  useEffect(() => {
    if (!isGeoDebugEnabled()) return;
    geoDebugLog(GEO_DEBUG_PREFIX, 'debug enabled', {
      devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : undefined,
      devicePixelRatioBucket,
      doubleClickWindowMs,
      singleClickDelayMs,
    });
  }, [devicePixelRatioBucket]);

  useEffect(() => {
    if (!isGeoDebugEnabled()) return;
    geoDebugLog(GEO_DEBUG_PREFIX, 'computed center', {
      center,
      fallbackCenter,
      markerCount: markerLocations.length,
      selectedLocationId,
      sampleMarkers: markerLocations.slice(0, 3).map((m) => ({
        id: m.location.id,
        lat: m.lat,
        lon: m.lon,
      })),
    });
  }, [center, fallbackCenter, markerLocations, selectedLocationId]);

  const getMarkerIcon = (location: GeoLocation): L.DivIcon => {
    const key = `${devicePixelRatioBucket}|${location.categoryIcon ?? ''}|${location.categoryColor ?? ''}|${location.isVirtualCoordinates ? 'virtual' : 'real'}`;
    const cached = markerIconByKey.get(key);
    if (cached) return cached;

    const IconComponent = getIconComponent(location.categoryIcon);
    const html = renderToStaticMarkup(
      <div className={location.isVirtualCoordinates ? 'geo-marker geo-marker--virtual' : 'geo-marker'}>
        <img className="geo-marker__shadow" src={markerShadow} alt="" />
        <img className="geo-marker__pin" src={devicePixelRatioBucket === '2x' ? markerIcon2x : markerIcon} alt="" />
        {IconComponent ? (
          <span className="geo-marker__overlay" style={{ color: location.categoryColor ?? '#fff' }}>
            <IconComponent />
          </span>
        ) : null}
      </div>
    );

    const icon = L.divIcon({
      html,
      className: 'geo-marker-wrapper',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      tooltipAnchor: [16, -28],
    });

    markerIconByKey.set(key, icon);
    return icon;
  };

  const MapClickHandler: React.FC = () => {
    useMapEvents({
      click: (event) => {
        geoDebugLog(GEO_DEBUG_PREFIX, 'map click', { lat: event.latlng.lat, lon: event.latlng.lng });
        if (onMapClick) {
          onMapClick(event.latlng.lat, event.latlng.lng);
          return;
        }
        onMapBackgroundClick?.(event.latlng.lat, event.latlng.lng);
      },
      dblclick: (event) => {
        geoDebugLog(GEO_DEBUG_PREFIX, 'map dblclick', { lat: event.latlng.lat, lon: event.latlng.lng });
      },
    });
    return null;
  };

  return (
    <div style={{ width: '100%', height, cursor: onMapClick ? 'crosshair' : undefined }}>
      <MapContainer
        center={center}
        zoom={16}
        scrollWheelZoom
        doubleClickZoom={false}
        inertia={false}
        zoomAnimation={false}
        fadeAnimation={false}
        markerZoomAnimation={false}
        style={{ width: '100%', height: '100%', borderRadius: 0 }}
      >
        <StopLeafletAnimationsOnUnmount />
        <InvalidateSizeOnContainerResize />
        <RecenterOnDataChange
          center={center}
          fallbackCenter={fallbackCenter}
          selectedLocationId={selectedLocationId}
          selectedLatLon={selectedLatLon}
          markerBounds={markerBounds}
          markerCount={markerLocations.length}
        />
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {onMapClick || onMapBackgroundClick ? <MapClickHandler /> : null}
        {markerLocations.map(({ location, lat, lon }) => (
          <Marker
            key={location.id}
            position={[lat, lon]}
            icon={getMarkerIcon(location)}
            draggable={canEdit}
            eventHandlers={{
              click: (event) => {
                const originalEvent = (event as LeafletMouseEvent).originalEvent;
                const state = doubleClickStateRef.current;
                const nowMs = Date.now();

                geoDebugLog(GEO_DEBUG_PREFIX, 'marker click raw', {
                  locationId: location.id,
                  type: (originalEvent as Event | undefined)?.type,
                  detail: (originalEvent as MouseEvent | undefined)?.detail,
                  nowMs,
                  stateLocationId: state.locationId,
                  suppressClickUntilMs: state.suppressClickUntilMs,
                });

                if (nowMs < state.suppressClickUntilMs) {
                  geoDebugLog(GEO_DEBUG_PREFIX, 'marker click suppressed', {
                    locationId: location.id,
                    nowMs,
                    suppressClickUntilMs: state.suppressClickUntilMs,
                  });
                  return;
                }

                if (originalEvent) {
                  L.DomEvent.stopPropagation(originalEvent);
                  L.DomEvent.preventDefault(originalEvent);
                }

                const clickDetail = typeof (originalEvent as MouseEvent | undefined)?.detail === 'number'
                  ? (originalEvent as MouseEvent).detail
                  : 1;
                if (clickDetail >= 2) {
                  geoDebugLog(GEO_DEBUG_PREFIX, 'marker dblclick (via detail)', {
                    locationId: location.id,
                    clickDetail,
                    nowMs,
                    prevLocationId: state.locationId,
                  });
                  if (state.pendingSingleClickTimerId !== null) {
                    window.clearTimeout(state.pendingSingleClickTimerId);
                    state.pendingSingleClickTimerId = null;
                    geoDebugLog(GEO_DEBUG_PREFIX, 'single click canceled (via detail)', { locationId: location.id });
                  }
                  if (state.timerId !== null) {
                    window.clearTimeout(state.timerId);
                    state.timerId = null;
                  }
                  state.locationId = null;
                  state.suppressClickUntilMs = nowMs + 200;
                  onLocationDoubleClick?.(location);
                  return;
                }

                if (state.locationId === location.id) {
                  geoDebugLog(GEO_DEBUG_PREFIX, 'marker dblclick (via 2nd click)', { locationId: location.id, nowMs });
                  if (state.pendingSingleClickTimerId !== null) {
                    window.clearTimeout(state.pendingSingleClickTimerId);
                    state.pendingSingleClickTimerId = null;
                    geoDebugLog(GEO_DEBUG_PREFIX, 'single click canceled (via 2nd click)', { locationId: location.id });
                  }
                  if (state.timerId !== null) {
                    window.clearTimeout(state.timerId);
                    state.timerId = null;
                  }
                  state.locationId = null;
                  state.suppressClickUntilMs = nowMs + 200;
                  onLocationDoubleClick?.(location);
                  return;
                }

                clearMarkerDoubleClickState();
                state.locationId = location.id;
                state.timerId = window.setTimeout(() => {
                  geoDebugLog(GEO_DEBUG_PREFIX, 'double click window expired', { locationId: location.id });
                  clearMarkerDoubleClickState();
                }, doubleClickWindowMs);

                state.pendingSingleClickTimerId = window.setTimeout(() => {
                  geoDebugLog(GEO_DEBUG_PREFIX, 'single click fired', { locationId: location.id });
                  onLocationClick?.(location);
                }, singleClickDelayMs);

                geoDebugLog(GEO_DEBUG_PREFIX, 'marker click armed', {
                  locationId: location.id,
                  clickDetail,
                  nowMs,
                  doubleClickWindowMs,
                  singleClickDelayMs,
                });
              },
              dblclick: (event) => {
                const originalEvent = (event as LeafletMouseEvent).originalEvent;
                if (originalEvent) {
                  L.DomEvent.stopPropagation(originalEvent);
                  L.DomEvent.preventDefault(originalEvent);
                }
                const state = doubleClickStateRef.current;
                const nowMs = Date.now();
                state.suppressClickUntilMs = nowMs + 200;
                geoDebugLog(GEO_DEBUG_PREFIX, 'marker dblclick (leaflet event)', { locationId: location.id, nowMs });
                if (state.pendingSingleClickTimerId !== null) {
                  window.clearTimeout(state.pendingSingleClickTimerId);
                  state.pendingSingleClickTimerId = null;
                  geoDebugLog(GEO_DEBUG_PREFIX, 'single click canceled (leaflet dblclick)', { locationId: location.id });
                }
                clearMarkerDoubleClickState();
                onLocationDoubleClick?.(location);
              },
              dragend: (event) => {
                if (!canEdit) return;
                if (!onLocationMove) return;
                const marker = event.target as L.Marker;
                const next = marker.getLatLng();
                onLocationMove(location, next.lat, next.lng);
              },
            }}
          >
            <Tooltip
              permanent
              direction="top"
              offset={[0, -12]}
              opacity={0.95}
              interactive={false}
              className={
                location.id === selectedLocationId
                  ? 'geo-location-label geo-location-label--selected'
                  : 'geo-location-label'
              }
            >
              {location.name}
            </Tooltip>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};
